import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import useSocket from "../../hooks/useSocket";
import eventEmitter from "../../utils/eventEmitter";
import { DownloadVideoModal } from "../MenuOptions";
import { DownloadTopBar } from "./DownloadTopBar";

export type TrackedDownloadStatus = "in_progress" | "ready" | "stale";

export interface TrackedDownload {
    /** Correlation id generated at submit time and echoed back by the zip pipeline. */
    roomId: string;
    /** Human-readable name shown in the bar (the zip file name). */
    label: string;
    status: TrackedDownloadStatus;
    /** Download URL, present once status is "ready". */
    fileName?: string;
    promotedAt: number;
    /** Whether the terms modal has already auto-opened for this download. */
    autoOpened: boolean;
}

interface DownloadsContextValue {
    /**
     * Hand a long-running zip over to the global tracker. Called from a
     * download button's timeout branch; from then on the top bar owns the
     * wait and the button can safely unmount.
     */
    promote: (roomId: string, label: string) => void;
}

const DownloadsContext = createContext<DownloadsContextValue>({
    promote: () => {}
});

export const useDownloads = () => useContext(DownloadsContext);

/** After this long with no completion event, show "taking longer than expected". */
const STALE_AFTER_MS = 15 * 60 * 1000;
const STALE_CHECK_INTERVAL_MS = 30 * 1000;

/**
 * App-root tracker for long-running zip downloads.
 *
 * Fast zips resolve on the HTTP response inside whichever button started
 * them. Slow zips time out at the gateway and finish via a websocket
 * `download` event; this provider owns that async leg so it survives
 * navigation: it mounts the app's single socket connection, keeps a
 * registry of promoted downloads, renders the slim top bar, and auto-opens
 * the terms modal when a download completes.
 */
export function DownloadsProvider({ children }: { children: React.ReactNode }) {
    // The app's single socket connection lives here (moved up from Home) so
    // completion events arrive on every route, including public share pages.
    useSocket();

    const [downloads, setDownloads] = useState<TrackedDownload[]>([]);
    const [modalRoomId, setModalRoomId] = useState<string | null>(null);

    // Mirror of `downloads` for the dev test aid below (which is registered once).
    const downloadsRef = useRef<TrackedDownload[]>([]);
    downloadsRef.current = downloads;

    const promote = useCallback((roomId: string, label: string) => {
        setDownloads((prev) => [
            ...prev.filter((d) => d.roomId !== roomId),
            {
                roomId,
                label,
                status: "in_progress",
                promotedAt: Date.now(),
                autoOpened: false
            }
        ]);
    }, []);

    // Resolve promoted downloads from the websocket completion event.
    // Strict match only: events without a roomId, or for a roomId we never
    // promoted, are ignored (completions are broadcast to every client).
    useEffect(() => {
        const handleDownloadEvent = (param: { fileName?: string; roomId?: string }) => {
            const { fileName, roomId } = param || {};
            if (!roomId || !fileName) return;
            setDownloads((prev) =>
                prev.map((d) => (d.roomId === roomId ? { ...d, status: "ready" as const, fileName } : d))
            );
        };
        eventEmitter.on("download", handleDownloadEvent);
        return () => {
            eventEmitter.off("download", handleDownloadEvent);
        };
    }, []);

    // Flag downloads that never resolve so the bar can say so instead of
    // spinning forever.
    const hasInProgress = downloads.some((d) => d.status === "in_progress");
    useEffect(() => {
        if (!hasInProgress) return;
        const timer = setInterval(() => {
            setDownloads((prev) =>
                prev.map((d) =>
                    d.status === "in_progress" && Date.now() - d.promotedAt > STALE_AFTER_MS
                        ? { ...d, status: "stale" as const }
                        : d
                )
            );
        }, STALE_CHECK_INTERVAL_MS);
        return () => clearInterval(timer);
    }, [hasInProgress]);

    // Auto-open the terms modal the first time a download becomes ready.
    // One at a time: if a modal is already open, the next ready download
    // opens when it closes. Closing without proceeding keeps the download
    // in the bar as a fallback so the link isn't lost.
    useEffect(() => {
        if (modalRoomId) return;
        const next = downloads.find((d) => d.status === "ready" && !d.autoOpened);
        if (!next) return;
        setDownloads((prev) => prev.map((d) => (d.roomId === next.roomId ? { ...d, autoOpened: true } : d)));
        setModalRoomId(next.roomId);
    }, [downloads, modalRoomId]);

    const remove = useCallback((roomId: string) => {
        setDownloads((prev) => prev.filter((d) => d.roomId !== roomId));
    }, []);

    // DEV-only test aid. The real completion event is fired when the zip
    // Lambda calls POST /brands/:id/zipfinished, and that callback targets a
    // fixed remote URL — it cannot reach a local backend, so long-running
    // downloads never resolve on a fully-local stack. Call
    // `window.__simulateZipDone()` in the browser console to resolve the most
    // recent in-flight download as if the server had emitted its event.
    useEffect(() => {
        if (process.env.NODE_ENV === "production") return;
        (window as any).__simulateZipDone = (
            fileName = "https://www.w3schools.com/html/mov_bbb.mp4",
        ) => {
            const latest = [...downloadsRef.current]
                .reverse()
                .find((d) => d.status === "in_progress" || d.status === "stale");
            if (!latest) {
                // eslint-disable-next-line no-console
                console.warn("[downloads] nothing in progress to resolve");
                return;
            }
            // eslint-disable-next-line no-console
            console.log("[downloads] simulating completion for roomId", latest.roomId);
            eventEmitter.emit("download", { roomId: latest.roomId, fileName });
        };
        return () => {
            delete (window as any).__simulateZipDone;
        };
    }, []);

    const modalDownload = downloads.find((d) => d.roomId === modalRoomId) || null;

    return (
        <DownloadsContext.Provider value={{ promote }}>
            <DownloadTopBar downloads={downloads} onOpen={setModalRoomId} onDismiss={remove} />
            {downloads.length > 0 && <div className="block h-11"></div>}
            {children}
            <DownloadVideoModal
                open={!!modalDownload}
                onClose={() => setModalRoomId(null)}
                onProceed={() => {
                    if (modalDownload) remove(modalDownload.roomId);
                    setModalRoomId(null);
                }}
                downloadSource={modalDownload?.fileName || ""}
            />
        </DownloadsContext.Provider>
    );
}
